import {
  alpha,
  ComponentsOverrides,
  ComponentsProps,
  ComponentsVariants,
  Theme,
} from "@mui/material";
import { Colors } from "../../theme";

export const getButtonOverrides = (colors: Colors) => ({
  defaultProps: {
    color: "primary",
    variant: "contained",
    size: "medium",
  } as ComponentsProps["MuiButton"],
  styleOverrides: {
    root: {
      fontWeight: 600,
      textTransform: "none",
      borderRadius: 6,
      boxShadow: "none",
      transition: "padding 0.2s cubic-bezier(0.22, 1, 0.36, 1)",
      ":hover, :active, :focus": {
        boxShadow: "none",
      },
    },
  } as ComponentsOverrides<Theme>["MuiButton"],
  variants: [
    {
      props: {
        size: "small",
      },
      style: {
        fontSize: 15,
        padding: "10px 20px",
      },
    },
    {
      props: {
        size: "medium",
      },
      style: {
        fontSize: 16,
        padding: "12px 24px",
      },
    },
    {
      props: {
        size: "large",
      },
      style: {
        fontSize: 18,
        padding: "20px 24px",
      },
    },
    {
      props: {
        variant: "text",
      },
      style: {
        backgroundColor: "transparent",
      },
    },
    {
      props: {
        variant: "contained",
      },
      style: {
        border: "none",
      },
    },
    {
      props: {
        variant: "invertedContained",
      },
      style: {
        border: "none",
        backgroundColor: colors.white,
        ":hover": {
          backgroundColor: alpha(colors.white, 0.9),
        },
      },
    },
    {
      props: {
        variant: "outlined",
      },
      style: {
        backgroundColor: "transparent",
      },
    },
    {
      props: {
        color: "primary",
        variant: "text",
      },
      style: {
        color: colors.champagneDark,
        ":hover": {
          color: colors.champagneDark,
          backgroundColor: alpha(colors.champagneDark, 0.1),
        },
      },
    },
    {
      props: {
        color: "primary",
        variant: "contained",
      },
      style: {
        color: colors.white,
        backgroundColor: colors.champagneDark,
        ":hover": {
          backgroundColor: alpha(colors.champagneDark, 0.9),
        },
      },
    },
    {
      props: {
        color: "primary",
        variant: "invertedContained",
      },
      style: {
        color: colors.champagneDark,
        ":hover": {
          color: colors.champagneDark,
        },
      },
    },
    {
      props: {
        color: "primary",
        variant: "outlined",
      },
      style: {
        color: colors.champagneDark,
        borderColor: colors.champagneDark,
        ":hover": {
          color: colors.white,
          backgroundColor: colors.champagneDark,
          borderColor: alpha(colors.champagneDark, 0.9),
        },
      },
    },
    {
      props: {
        color: "secondary",
        variant: "text",
      },
      style: {
        color: colors.oliveDark,
        ":hover": {
          color: colors.oliveDark,
          backgroundColor: alpha(colors.oliveDark, 0.1),
        },
      },
    },
    {
      props: {
        color: "secondary",
        variant: "contained",
      },
      style: {
        color: colors.white,
        backgroundColor: colors.oliveDark,
        ":hover": {
          backgroundColor: alpha(colors.oliveDark, 0.9),
        },
      },
    },
    {
      props: {
        color: "secondary",
        variant: "invertedContained",
      },
      style: {
        color: colors.oliveDark,
        ":hover": {
          color: colors.oliveDark,
        },
      },
    },
    {
      props: {
        color: "secondary",
        variant: "outlined",
      },
      style: {
        color: colors.oliveDark,
        borderColor: colors.oliveDark,
        ":hover": {
          color: colors.white,
          backgroundColor: colors.oliveDark,
          borderColor: alpha(colors.oliveDark, 0.9),
        },
      },
    },
    {
      props: {
        color: "info",
        variant: "text",
      },
      style: {
        color: colors.champagneDisable,
        ":hover": {
          color: colors.champagneDisable,
          backgroundColor: alpha(colors.champagneDisable, 0.1),
        },
      },
    },
    {
      props: {
        color: "info",
        variant: "contained",
      },
      style: {
        color: colors.white,
        backgroundColor: colors.champagneDisable,
        ":hover": {
          backgroundColor: alpha(colors.champagneDisable, 0.9),
        },
      },
    },
    {
      props: {
        color: "info",
        variant: "invertedContained",
      },
      style: {
        color: colors.champagneDisable,
        ":hover": {
          color: colors.champagneDisable,
        },
      },
    },
    {
      props: {
        color: "info",
        variant: "outlined",
      },
      style: {
        color: colors.champagneDisable,
        borderColor: colors.champagneDisable,
        ":hover": {
          color: colors.white,
          backgroundColor: colors.champagneDisable,
          borderColor: alpha(colors.champagneDisable, 0.9),
        },
      },
    },
    {
      props: {
        color: "success",
        variant: "text",
      },
      style: {
        color: colors.white,
        ":hover": {
          color: colors.white,
          backgroundColor: alpha(colors.white, 0.1),
        },
      },
    },
    {
      props: {
        color: "success",
        variant: "contained",
      },
      style: {
        color: colors.champagneDark,
        backgroundColor: colors.white,
        ":hover": {
          backgroundColor: alpha(colors.white, 0.9),
        },
      },
    },
    {
      props: {
        color: "success",
        variant: "invertedContained",
      },
      style: {
        color: colors.white,
        backgroundColor: colors.champagneDark,
        ":hover": {
          color: colors.white,
          backgroundColor: alpha(colors.champagneDark, 0.9),
        },
      },
    },
    {
      props: {
        color: "success",
        variant: "outlined",
      },
      style: {
        color: colors.white,
        borderColor: colors.white,
        ":hover": {
          color: colors.champagneDark,
          backgroundColor: colors.white,
          borderColor: alpha(colors.white, 0.9),
        },
      },
    },
  ] as ComponentsVariants["MuiButton"],
});
